import type { App } from '@hangar/shared';
import { ApiError } from '@/api/client';
import { useEasSetup, useProvisionEas } from '@/api/hooksEas';
import { stagger } from '@/lib/motion';
import { useCanConfigure } from '@/lib/permissions';
import { useSeesProviders } from '@/lib/useAudience';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { StatusPill } from '@/components/badges';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { appJsonSnippet, hasEasProject } from '@/features/apps/easSetupModel';
import { AppJson, EasProjectCard } from './EasProjectCard';
import { CLIENT_APP_JSON_NOTE, nativeSetupNote } from './appPageModel';

/**
 * How the app gets built (SPEC decision 23): platform operators see the provider's card (the EAS project); clients see
 * a neutral "Build setup" that is ready or being prepared, plus what their own app.json must carry.
 */
export function BuildSetupCard({ app }: { app: App }) {
  return useSeesProviders() ? <EasProjectCard app={app} /> : <ClientBuildSetup app={app} />;
}

function SetupCard({ ready, children }: { ready: boolean | undefined; children: React.ReactNode }) {
  return (
    <Card className="enter" style={stagger(2)} data-testid="app-build-setup">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="rocket_launch" size={18} className="text-subtle" />
          Build setup
        </CardTitle>
        <CardDescription>Hangar prepares and manages how this app is built: nothing to set up outside Hangar</CardDescription>
        {ready === undefined ? null : (
          <CardAction>
            <StatusPill status={ready ? 'succeeded' : 'queued'} label={ready ? 'Ready' : 'Being prepared'} still />
          </CardAction>
        )}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function ClientBuildSetup({ app }: { app: App }) {
  if (!hasEasProject(app.framework)) {
    return (
      <SetupCard ready={Boolean(app.bitriseAppSlug)}>
        <p className="text-[13px] text-muted-foreground" data-testid="app-build-setup-note">{nativeSetupNote(Boolean(app.bitriseAppSlug))}</p>
      </SetupCard>
    );
  }
  return <ManagedSetup app={app} />;
}

/** An Expo / React Native app: ready (with the app.json to commit) or being prepared (with the one way to start it). */
function ManagedSetup({ app }: { app: App }) {
  const setup = useEasSetup(app.id);
  if (setup.error instanceof ApiError && (setup.error.status === 404 || setup.error.status === 501)) return null;
  const snippet = setup.data ? appJsonSnippet(setup.data) : null;
  return (
    <SetupCard ready={setup.data?.configured}>
      {setup.isLoading && !setup.data ? <Skeleton className="h-16 w-full" aria-busy="true" /> : null}
      {setup.isError && !setup.data ? <ErrorState compact error={setup.error} onRetry={() => void setup.refetch()} /> : null}
      {setup.data?.configured && snippet ? <AppJson snippet={snippet} manual={false} note={CLIENT_APP_JSON_NOTE} /> : null}
      {setup.data && !setup.data.configured ? <StartSetup app={app} /> : null}
    </SetupCard>
  );
}

/** Not prepared yet: the same `POST /apps/{id}/eas/provision` operators run, worded for the client. */
function StartSetup({ app }: { app: App }) {
  const can = useCanConfigure(app.projectId);
  const provision = useProvisionEas();
  return (
    <div className="flex flex-col gap-3" data-testid="app-build-setup-start">
      <p className="text-[13px] text-muted-foreground">Hangar has not prepared the builds of this app yet. Set them up now, or your Hangar administrators do it for you.</p>
      <ProblemAlert error={provision.error} testId="app-build-setup-error" />
      {app.archivedAt ? null : (
        <GatedButton can={can} loading={provision.isPending} className="w-fit" data-testid="app-build-setup-button" onClick={() => provision.mutate({ appId: app.id })}>
          <Icon name={provision.error ? 'refresh' : 'add'} size={18} />
          {provision.error ? 'Try again' : 'Set up builds'}
        </GatedButton>
      )}
    </div>
  );
}
